import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.tsx';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Icons,
  PageHeader,
  Spinner,
} from '../../components/ui.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useBeginTotp, useChangePassword, useConfirmTotp } from '../../lib/profile-queries.ts';

/**
 * Password and two-factor.
 *
 * Both controls here weaken or strengthen the account itself, so both re-ask
 * for the password: holding a session proves the browser was left unlocked,
 * not that the person at the keyboard is the account holder.
 */
export function SecurityPage() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div className="h-full overflow-y-auto pb-10">
      <PageHeader
        title="Security"
        description="Your password and the second factor asked for when you sign in."
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <PasswordCard />
        <TwoFactorCard enabled={user.totpEnabled} />
      </div>
    </div>
  );
}

function PasswordCard() {
  const change = useChangePassword();
  const toast = useToast();
  const { logout } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const problems = passwordProblems(next);
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = current.length > 0 && problems.length === 0 && next === confirm;

  async function submit() {
    setError(null);
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      // The server ends every session on a password change, including this
      // one. Saying so and signing out is honest; leaving the user to
      // discover it on their next click is not.
      toast.success('Password changed', 'You have been signed out everywhere. Sign in again.');
      await logout();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change the password.');
    }
  }

  return (
    <Card title="Password" description="Changing it signs you out on every device.">
      <div className="space-y-4">
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />

        {next ? (
          <ul className="space-y-1">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(next);
              return (
                <li
                  key={rule.label}
                  className={`flex items-center gap-2 text-micro ${met ? 'text-[var(--success)]' : 'text-muted'}`}
                >
                  <span aria-hidden>{met ? '✓' : '○'}</span>
                  {rule.label}
                </li>
              );
            })}
          </ul>
        ) : null}

        <Field
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          {...(mismatch ? { error: 'These do not match.' } : {})}
        />

        {error ? <Alert>{error}</Alert> : null}

        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={!ready || change.isPending}
        >
          {change.isPending ? <Spinner label="Changing" /> : 'Change password'}
        </Button>
      </div>
    </Card>
  );
}

const PASSWORD_RULES = [
  { label: 'At least 12 characters', test: (value: string) => value.length >= 12 },
  { label: 'An upper case letter', test: (value: string) => /[A-Z]/.test(value) },
  { label: 'A lower case letter', test: (value: string) => /[a-z]/.test(value) },
  { label: 'A digit', test: (value: string) => /\d/.test(value) },
];

function passwordProblems(value: string): string[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(value)).map((rule) => rule.label);
}

/**
 * Two-factor, as a switch with the whole enrolment behind it.
 *
 * Turning it on is three steps — scan, prove, keep the backup codes — and all
 * three have to complete before the account is actually protected. The server
 * holds the seed but leaves `totpEnabled` false until a code is verified, so
 * abandoning halfway leaves the account exactly as it was rather than locked
 * out of itself.
 */
function TwoFactorCard({ enabled }: { enabled: boolean }) {
  const { reload } = useAuth();
  const dialog = useDialog();
  const toast = useToast();
  const begin = useBeginTotp();
  const confirmCode = useConfirmTotp();

  const backupCodes = useQuery({
    queryKey: ['backup-codes-count'],
    queryFn: () => api.get<{ remaining: number }>('/api/auth/totp/backup-codes/count'),
    enabled,
  });

  const [step, setStep] = useState<'idle' | 'scan'>('idle');
  const [enrolment, setEnrolment] = useState<{
    secret: string;
    otpauth: string;
    qrCodeDataUrl: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setError(null);
    try {
      const result = await begin.mutateAsync();
      setEnrolment(result as never);
      setStep('scan');
      setCode('');
    } catch (caught) {
      toast.error(
        'Could not start setup',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function finish() {
    setError(null);
    try {
      const { backupCodes: codes } = await confirmCode.mutateAsync(code);
      setStep('idle');
      setEnrolment(null);
      await reload();
      await showBackupCodes(codes);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'That code was not accepted. Try the current one.',
      );
    }
  }

  async function showBackupCodes(codes: string[]) {
    await dialog.notify({
      title: 'Two-factor is on. Save these backup codes.',
      message: [
        'Each code signs you in once if you lose your phone. They are shown now and never again.',
        '',
        codes.join('    '),
        '',
        'Print them or put them in a password manager. Without your phone and without these, only an owner can get you back in.',
      ].join('\n'),
    });
  }

  async function turnOff() {
    const password = await dialog.prompt({
      title: 'Turn off two-factor?',
      message:
        'Your account will be protected by its password alone. Enter your password to confirm.',
      label: 'Password',
      type: 'password',
      confirmLabel: 'Turn off',
      tone: 'danger',
    });
    if (!password) return;

    try {
      await api.post('/api/auth/totp/disable', { password });
      await reload();
      toast.success('Two-factor turned off');
    } catch (caught) {
      toast.error(
        'Could not turn it off',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function regenerate() {
    const password = await dialog.prompt({
      title: 'Generate new backup codes?',
      message: 'The codes you have now stop working immediately. Enter your password to confirm.',
      label: 'Password',
      type: 'password',
      confirmLabel: 'Generate',
    });
    if (!password) return;

    try {
      const { backupCodes: codes } = await api.post<{ backupCodes: string[] }>(
        '/api/auth/totp/backup-codes',
        { password },
      );
      await backupCodes.refetch();
      await showBackupCodes(codes);
    } catch (caught) {
      toast.error(
        'Could not generate new codes',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  return (
    <Card
      title="Two-factor authentication"
      description="A six-digit code from your phone, on top of your password."
      actions={enabled ? <Badge tone="success">On</Badge> : <Badge>Off</Badge>}
    >
      {enabled ? (
        <div className="space-y-4">
          <p className="text-body-sm text-body">
            When you sign in, this system asks for a code from your authenticator app after your
            password.
          </p>

          <div className="rounded-lg border border-border bg-sunken px-4 py-3">
            <p className="text-body-sm text-ink">
              {backupCodes.data
                ? `${backupCodes.data.remaining} backup ${backupCodes.data.remaining === 1 ? 'code' : 'codes'} unused`
                : 'Backup codes'}
            </p>
            <p className="mt-0.5 text-micro text-muted">
              Each one signs you in once if you lose your phone.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button icon={<Icons.Key />} onClick={() => void regenerate()}>
              New backup codes
            </Button>
            <Button variant="ghost" onClick={() => void turnOff()}>
              Turn off
            </Button>
          </div>
        </div>
      ) : step === 'idle' ? (
        <div className="space-y-4">
          <p className="text-body-sm text-body">
            Without it, anyone who learns your password is you. With it, they also need the phone in
            your pocket.
          </p>
          <ol className="space-y-1.5 text-body-sm text-muted">
            <li>1. Install an authenticator app — Google Authenticator, Authy, 1Password.</li>
            <li>2. Scan the code this page shows you.</li>
            <li>3. Type the six digits back to prove it worked.</li>
          </ol>
          <Button
            variant="primary"
            icon={<Icons.Security />}
            onClick={() => void start()}
            disabled={begin.isPending}
          >
            {begin.isPending ? <Spinner label="Preparing" /> : 'Set up two-factor'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start gap-5">
            {enrolment?.qrCodeDataUrl ? (
              <img
                src={enrolment.qrCodeDataUrl}
                alt="QR code for your authenticator app"
                className="h-44 w-44 rounded-lg border border-border bg-white p-1.5"
              />
            ) : (
              <Spinner label="Generating the code" />
            )}

            <div className="min-w-0 flex-1">
              <p className="text-body-sm text-body">
                Scan this with your authenticator app, then enter the six digits it shows.
              </p>
              <p className="mt-3 text-micro text-muted">Can’t scan? Type this key in by hand:</p>
              <code className="mt-1 block break-all rounded-md border border-border bg-sunken px-3 py-2 font-mono text-body-sm text-ink">
                {enrolment?.secret}
              </code>
            </div>
          </div>

          <Field
            label="Six-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            className="font-mono tracking-[0.35em]"
          />

          {error ? <Alert>{error}</Alert> : null}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => void finish()}
              disabled={code.length !== 6 || confirmCode.isPending}
            >
              {confirmCode.isPending ? <Spinner label="Verifying" /> : 'Verify and turn on'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setStep('idle');
                setEnrolment(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
